import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import type { UploadUploadedFiles } from '@saviynt/design-system';
import { Link as LinkButton, Typography, Upload } from '@saviynt/design-system';

import { useLocalization } from '../../../../hooks';
import { useTypedSelector } from '../../../../hooks/use-typedselector';
import Logger from '../../../../services/loggerService';
import { type RootState } from '../../../../services/serviceTypes';
import {
  FILE_ERRORS,
  MAX_FILE_COUNT,
  MAX_FILE_SIZE,
  MAX_TOTAL_SIZE,
  UPLOAD_ERROR_MESSAGES,
} from '../../../../utils';
import FileDownloadApi from '../../data-layer/fileDownloadApi';
import {
  resetDiscoveryFileUploadState,
  setDiscoveryFileUploadError,
  setIsFileValid,
  setUploadedFile,
} from '../../data-store/DiscoveryFileUploadSlice';

import './FileUploadModalContent.css';

interface ExtendedUploadFile extends UploadUploadedFiles {
  fileObject?: File;
}

/**
 * Component for uploading discovery source file and naming it.
 */
function FileUploadModalContent() {
  const dispatch = useDispatch();
  const formatMessage = useLocalization();
  const [uploadedFiles, setUploadedFiles] = useState<ExtendedUploadFile[]>([]);
  const [reuploadFileName, setReuploadFileName] = useState<string>('');
  const [isMaxNumberUploaded, setIsMaxNumberUploaded] = useState(false);
  const [isMaxTotalSizeReached, setIsMaxTotalSizeReached] = useState(false);

  const { error: fileUploadError, uploadedFile } = useTypedSelector(
    (state: RootState) => state.discoveryFileUpload
  );

  const messageCode = fileUploadError?.message ?? '';
  let uploadErrorMessage = '';

  if (messageCode && UPLOAD_ERROR_MESSAGES[messageCode]) {
    uploadErrorMessage = UPLOAD_ERROR_MESSAGES[messageCode];
  } else if (messageCode) {
    uploadErrorMessage = 'Upload error occurred. Try again.';
  }

  const isUploadErrorFileRelated =
    FILE_ERRORS.includes(messageCode) ||
    uploadErrorMessage === 'Upload error occurred. Try again.';

  const handleFileSelect = (files: File[]) => {
    if (!files.length) {
      return;
    }

    const file = files[0];
    const fileSize = file.size;
    const fileName = file.name;

    if (fileSize > MAX_FILE_SIZE) {
      setUploadedFiles([
        {
          fileName,
          fileSize,
          isError: true,
          errorMessage: 'Upload Error: Exceeds max 2 MB size',
        },
      ]);
      dispatch(setUploadedFile(null));
      dispatch(setIsFileValid(false));

      return;
    }

    if (files.length > MAX_FILE_COUNT) {
      setIsMaxNumberUploaded(true);
      dispatch(setUploadedFile(null));
      dispatch(setIsFileValid(false));

      return;
    }

    if (fileSize > MAX_TOTAL_SIZE) {
      setIsMaxTotalSizeReached(true);
      dispatch(setUploadedFile(null));
      dispatch(setIsFileValid(false));

      return;
    }

    setUploadedFiles([
      {
        fileName,
        fileSize,
        fileObject: file,
      },
    ]);

    dispatch(setUploadedFile(file));
    setIsMaxNumberUploaded(false);
    setIsMaxTotalSizeReached(false);
    setReuploadFileName('');
    dispatch(setDiscoveryFileUploadError(null));
  };

  const handleFileDelete = (fileNameToDelete: string) => {
    setUploadedFiles((prev) =>
      prev.filter((file) => file.fileName !== fileNameToDelete)
    );
    dispatch(resetDiscoveryFileUploadState());
    setReuploadFileName('');
  };

  const handleFileReupload = (fileName: string) => {
    setReuploadFileName(fileName);
    dispatch(resetDiscoveryFileUploadState());
  };

  const handleDownloadSampleFile = async () => {
    try {
      await FileDownloadApi();
    } catch (error) {
      Logger.error('Sample download failed:', error);
    }
  };

  const shouldSetValid = useMemo(() => {
    const hasUpload = uploadedFiles.length > 0;
    const hasBackendError = isUploadErrorFileRelated;

    return hasUpload && !hasBackendError;
  }, [uploadedFiles.length, isUploadErrorFileRelated]);

  useEffect(() => {
    dispatch(setIsFileValid(shouldSetValid));
  }, [shouldSetValid, dispatch]);

  useEffect(() => {
    if (!uploadedFile) {
      setUploadedFiles([]);
    }
  }, [uploadedFile]);

  return (
    <section className='FileSourceModalContent'>
      <div className='FileSourceModalContent-importContent'>
        <Typography
          kind='h3'
          color='--color-foreground-neutral-bold'
          className='FileSourceModalContent-subtitle'>
          {formatMessage(
            'INTEGRATIONS.FILEUPLOAD.HEADING',
            'Import your discovery source from a file'
          )}
        </Typography>

        <div className='FileSourceModalContent-uploadLink'>
          <Upload
            kind='dropzone'
            isMultiple={false}
            onFileSelect={handleFileSelect}
            onFileDelete={handleFileDelete}
            onFileReupload={handleFileReupload}
            uploadedFiles={uploadedFiles.map((file) => ({
              ...file,
              isError: isUploadErrorFileRelated || file.isError,
              errorMessage: isUploadErrorFileRelated
                ? uploadErrorMessage
                : file.errorMessage,
            }))}
            acceptTypes='.xls,.xlsx'
            reuploadFileName={reuploadFileName}
            isMaxNumberUploaded={isMaxNumberUploaded}
            isMaxTotalSizeReached={isMaxTotalSizeReached}
            maxFileCount={MAX_FILE_COUNT}
            maxFileSize={MAX_FILE_SIZE / (1024 * 1024)}
            maxTotalFileSize={MAX_TOTAL_SIZE / (1024 * 1024)}
            dataTestId='aob-fileupload-fileinput'
          />
        </div>

        {uploadedFiles.length === 0 && (
          <LinkButton
            onClick={handleDownloadSampleFile}
            prefixIconKind='Download'
            text={formatMessage(
              'INTEGRATIONS.FILEUPLOAD.DOWNLOAD_SAMPLE',
              'Download Sample File'
            )}
            kind='button'
            size='small'
            dataTestId='aob-fileupload-download-samplefile'
          />
        )}

        {uploadedFiles.length > 0 && (
          <div className='FileUpload-Name-Screen'>
            <Typography kind='h3'>Discovery Source Name :</Typography>
            <Typography
              kind='body2'
              className='FileSourceModalContent-staticFilename'
              data-testid='aob-fileupload-static-filename'>
              File Upload Discovery Source
            </Typography>
          </div>
        )}
      </div>
    </section>
  );
}

export default FileUploadModalContent;
